import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  Grid,
  Tooltip,
  CircularProgress,
  Checkbox,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import CustomFieldsPanel from "./CustomFieldsPanel";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import api from "../../utils/api";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// ─── Constants ───────────────────────────────────────────────────────────────

const INTEGRATION_TYPES = {
  jonas_jcm: "Jonas JCM",
  jonas_encore: "Jonas Encore",
  quickbooks: "QuickBooks",
  manual: "Manual Export",
};

const TYPE_COLORS = {
  jonas_jcm: { bg: "rgba(47,99,173,0.1)", color: "#2f63ad" },
  jonas_encore: { bg: "rgba(34,197,94,0.1)", color: "#16a34a" },
  quickbooks: { bg: "rgba(234,179,8,0.1)", color: "#b45309" },
  manual: { bg: "rgba(107,119,140,0.1)", color: "#6b778c" },
};

const FREQUENCY_LABELS = {
  manual: "Manual",
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
};

const MODULES = [
  {
    key: "workorder",
    label: "Work Orders",
    endpoint: "/workorder-types",
    labelKey: "type",
  },
  {
    key: "violation",
    label: "Violations",
    endpoint: "/getCcrViolationTypes",
    labelKey: "title",
  },
  {
    key: "architectural",
    label: "Architectural",
    endpoint: "/architectural-request-types",
    labelKey: "type",
  },
];

const BLANK_INTEGRATION = {
  name: "",
  type: "jonas_jcm",
  is_active: true,
  frequency: "manual",
  export_hour: 0,
  export_minute: 0,
};

// ─── Shared styles ────────────────────────────────────────────────────────────

const paperSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.98)",
};

const dialogPaperSx = {
  borderRadius: "18px",
  background: "rgba(255,255,255,0.96)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  overflow: "hidden",
};

const fieldSx = {
  mt: 1.5,
  "& .MuiOutlinedInput-root": { backgroundColor: "#fff", borderRadius: "10px" },
};

const primaryBtnSx = {
  backgroundColor: "#2f63ad",
  color: "#fff",
  fontWeight: 700,
  borderRadius: "10px",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};

const secondaryBtnSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
};

// ─── IntegrationCard ──────────────────────────────────────────────────────────

const IntegrationCard = ({ integration, onEdit, onDelete, onExport, exporting }) => {
  const tc = TYPE_COLORS[integration.type] || TYPE_COLORS.manual;
  const exportTime = `${String(integration.export_hour ?? 0).padStart(2, "0")}:${String(
    integration.export_minute ?? 0
  ).padStart(2, "0")}`;

  return (
    <Paper
      elevation={0}
      sx={{
        ...paperSx,
        p: 2.5,
        position: "relative",
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.1)" },
      }}
    >
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: tc.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IntegrationInstructionsIcon sx={{ color: tc.color, fontSize: 22 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.3 }}
            noWrap
          >
            {integration.name}
          </Typography>
          <Chip
            label={INTEGRATION_TYPES[integration.type] || integration.type}
            size="small"
            sx={{
              mt: 0.5,
              fontWeight: 700,
              fontSize: "0.72rem",
              height: 22,
              backgroundColor: tc.bg,
              color: tc.color,
              borderRadius: "6px",
            }}
          />
        </Box>

        <Chip
          label={integration.is_active ? "Active" : "Inactive"}
          size="small"
          color={integration.is_active ? "success" : "default"}
          variant={integration.is_active ? "filled" : "outlined"}
          sx={{ flexShrink: 0, fontWeight: 600 }}
        />
      </Box>

      {/* Meta row */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="caption" sx={{ color: "#9aa5b4", display: "block" }}>
            Frequency
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#3a4a5c" }}>
            {FREQUENCY_LABELS[integration.frequency] || integration.frequency}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: "#9aa5b4", display: "block" }}>
            Export Time
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#3a4a5c" }}>
            {exportTime}
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Divider sx={{ mb: 1.5 }} />
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Tooltip title="Download export file">
          <span>
            <IconButton
              size="small"
              onClick={() => onExport(integration)}
              disabled={exporting === integration.id}
              sx={{ color: "#2f63ad" }}
            >
              {exporting === integration.id ? (
                <CircularProgress size={18} />
              ) : (
                <DownloadIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Edit integration">
          <IconButton size="small" onClick={() => onEdit(integration)} sx={{ color: "#2f63ad" }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete integration">
          <IconButton size="small" color="error" onClick={() => onDelete(integration)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

// ─── ItemGroupMappingsPanel ───────────────────────────────────────────────────

const ItemGroupMappingsPanel = ({ integrations }) => {
  const [selectedIntegrationId, setSelectedIntegrationId] = useState("");
  const [moduleTab, setModuleTab] = useState(0);
  const [moduleTypes, setModuleTypes] = useState({});
  const [mappings, setMappings] = useState([]);
  const [editedCodes, setEditedCodes] = useState({});
  const [newMapping, setNewMapping] = useState({ type_id: "", item_group_code: "", apply_to_all: false, line_item_type: "", subtype_id: "" });
  const [subtypes, setSubtypes] = useState([]);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const currentModule = MODULES[moduleTab];

  useEffect(() => {
    // Pre-fetch all module types
    MODULES.forEach(async (mod) => {
      try {
        const res = await api.get(mod.endpoint);
        setModuleTypes((prev) => ({ ...prev, [mod.key]: res.data || [] }));
      } catch {
        setModuleTypes((prev) => ({ ...prev, [mod.key]: [] }));
      }
    });
  }, []);

  useEffect(() => {
    if (selectedIntegrationId) fetchMappings();
    else setMappings([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIntegrationId, moduleTab]);

  useEffect(() => {
    if (!newMapping.type_id || newMapping.apply_to_all) {
      setSubtypes([]);
      return;
    }
    const fetchSubtypes = async () => {
      try {
        const mod = currentModule.key;
        let res;
        if (mod === "workorder") {
          res = await api.get(`/workorder-subtypes`, { params: { type_id: newMapping.type_id } });
        } else if (mod === "architectural") {
          res = await api.get(`/architectural-request-subtypes`, { params: { type_id: newMapping.type_id } });
        } else {
          setSubtypes([]);
          return;
        }
        setSubtypes(res.data || []);
      } catch {
        setSubtypes([]);
      }
    };
    fetchSubtypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newMapping.type_id, newMapping.apply_to_all, moduleTab]);

  const fetchMappings = async () => {
    try {
      const res = await api.get("/item-group-mappings", {
        params: { module: currentModule.key, integration_id: selectedIntegrationId },
      });
      setMappings(res.data || []);
    } catch {
      showSnack("Failed to load mappings", "error");
    }
  };

  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  const handleAdd = async () => {
    if (!newMapping.apply_to_all && !newMapping.type_id) {
      showSnack("Select a type or enable 'Apply to All'", "warning");
      return;
    }
    if (!newMapping.item_group_code.trim()) {
      showSnack("Enter an item group code", "warning");
      return;
    }
    try {
      await api.post("/item-group-mappings", {
        integration_id: Number(selectedIntegrationId),
        module: currentModule.key,
        type_id: newMapping.apply_to_all ? null : Number(newMapping.type_id),
        apply_to_all: newMapping.apply_to_all ? 1 : 0,
        item_group_code: newMapping.item_group_code.trim(),
        line_item_type: newMapping.line_item_type || null,
        subtype_id: newMapping.subtype_id || null,
      });
      setNewMapping({ type_id: "", item_group_code: "", apply_to_all: false, line_item_type: "", subtype_id: "" });
      showSnack("Mapping added");
      fetchMappings();
    } catch {
      showSnack("Error adding mapping", "error");
    }
  };

  const handleUpdate = async (mapping) => {
    const code = editedCodes[mapping.id] ?? mapping.item_group_code;
    try {
      await api.put(`/item-group-mappings/${mapping.id}`, {
        integration_id: mapping.integration_id,
        module: currentModule.key,
        type_id: mapping.type_id,
        apply_to_all: mapping.apply_to_all,
        item_group_code: code,
      });
      setEditedCodes((prev) => { const c = { ...prev }; delete c[mapping.id]; return c; });
      showSnack("Mapping updated");
      fetchMappings();
    } catch {
      showSnack("Error updating mapping", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this mapping?")) return;
    try {
      await api.delete(`/item-group-mappings/${id}`);
      showSnack("Mapping deleted");
      fetchMappings();
    } catch {
      showSnack("Error deleting mapping", "error");
    }
  };

  const types = moduleTypes[currentModule.key] || [];

  const getTypeLabel = (typeId) => {
    if (!typeId) return "All Types (catch-all)";
    const t = types.find((x) => Number(x.id) === Number(typeId));
    return t ? t[currentModule.labelKey] : `ID #${typeId}`;
  };

  return (
    <Box>
      {/* Integration selector */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          select
          label="Integration"
          value={selectedIntegrationId}
          onChange={(e) => setSelectedIntegrationId(e.target.value)}
          size="small"
          sx={{ minWidth: 260, ...fieldSx, mt: 0 }}
        >
          <MenuItem value="">-- Select Integration --</MenuItem>
          {integrations.map((i) => (
            <MenuItem key={i.id} value={i.id}>
              {i.name} ({INTEGRATION_TYPES[i.type] || i.type})
            </MenuItem>
          ))}
        </TextField>

        {selectedIntegrationId && (
          <IconButton size="small" onClick={fetchMappings} title="Refresh">
            <RefreshIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {!selectedIntegrationId ? (
        <Alert severity="info" sx={{ borderRadius: "10px" }}>
          Select an integration above to manage its item group mappings.
        </Alert>
      ) : (
        <>
          {/* Module tabs */}
          <Tabs
            value={moduleTab}
            onChange={(_, v) => setModuleTab(v)}
            sx={{
              mb: 2,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 38 },
              "& .Mui-selected": { color: "#2f63ad !important", backgroundColor: "rgba(47,99,173,0.07)", borderRadius: "8px" },
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            {MODULES.map((m, i) => (
              <Tab key={i} label={m.label} />
            ))}
          </Tabs>

          {/* Add new mapping */}
          <Paper elevation={0} sx={{ ...paperSx, p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 1.5 }}>
              Add Mapping. {currentModule.label}
            </Typography>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={newMapping.apply_to_all}
                    onChange={(e) =>
                      setNewMapping((p) => ({ ...p, apply_to_all: e.target.checked, type_id: "" }))
                    }
                  />
                }
                label="Apply to All"
                sx={{ mr: 0 }}
              />

              {!newMapping.apply_to_all && (
                <TextField
                  select
                  size="small"
                  label={`${currentModule.label} Type`}
                  value={newMapping.type_id}
                  onChange={(e) => setNewMapping((p) => ({ ...p, type_id: e.target.value }))}
                  sx={{ minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                >
                  <MenuItem value="">Select Type</MenuItem>
                  {types.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t[currentModule.labelKey]}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                size="small"
                label="Item Group Code"
                value={newMapping.item_group_code}
                onChange={(e) => setNewMapping((p) => ({ ...p, item_group_code: e.target.value }))}
                sx={{ minWidth: 180, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />

              <FormControl size="small" sx={{ minWidth: 160, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <InputLabel>Line Item Type</InputLabel>
                <Select
                  value={newMapping.line_item_type || ""}
                  onChange={(e) => setNewMapping((p) => ({ ...p, line_item_type: e.target.value }))}
                  label="Line Item Type"
                >
                  <MenuItem value="">All (default)</MenuItem>
                  <MenuItem value="labor">Labor</MenuItem>
                  <MenuItem value="materials">Materials</MenuItem>
                  <MenuItem value="fine">Fine</MenuItem>
                  <MenuItem value="fee">Fee</MenuItem>
                </Select>
              </FormControl>

              {newMapping.type_id && !newMapping.apply_to_all && (
                <FormControl size="small" sx={{ minWidth: 180, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                  <InputLabel>Subtype (optional)</InputLabel>
                  <Select
                    value={newMapping.subtype_id || ""}
                    onChange={(e) => setNewMapping((p) => ({ ...p, subtype_id: e.target.value || "" }))}
                    label="Subtype (optional)"
                  >
                    <MenuItem value="">None (all subtypes)</MenuItem>
                    {subtypes.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.subtype_name || s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                sx={{ ...primaryBtnSx, height: 40 }}
              >
                Add
              </Button>
            </Box>
          </Paper>

          {/* Existing mappings */}
          <Paper elevation={0} sx={{ ...paperSx, overflow: "hidden" }}>
            {mappings.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: "#9aa5b4" }}>
                  No mappings found for this integration + module.
                </Typography>
              </Box>
            ) : (
              mappings.map((mapping) => (
                <Box
                  key={mapping.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 2.5,
                    py: 1.5,
                    borderBottom: "1px solid rgba(47,99,173,0.06)",
                    "&:last-child": { borderBottom: "none" },
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
                      {mapping.apply_to_all ? (
                        <Chip
                          label="Catch-all"
                          size="small"
                          sx={{
                            fontWeight: 700,
                            backgroundColor: "rgba(234,179,8,0.12)",
                            color: "#b45309",
                            borderRadius: "6px",
                          }}
                        />
                      ) : (
                        getTypeLabel(mapping.type_id)
                      )}
                    </Typography>
                  </Box>

                  <TextField
                    size="small"
                    label="Item Group Code"
                    value={editedCodes[mapping.id] ?? mapping.item_group_code}
                    onChange={(e) =>
                      setEditedCodes((prev) => ({ ...prev, [mapping.id]: e.target.value }))
                    }
                    sx={{ minWidth: 180, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />

                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleUpdate(mapping)}
                      disabled={!(mapping.id in editedCodes)}
                      sx={{ color: "#2f63ad" }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(mapping.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── MemberFilteringPanel ─────────────────────────────────────────────────────

const MemberFilteringPanel = () => {
  const [configs, setConfigs] = useState([]);
  const [edited, setEdited] = useState({});
  const [newCfg, setNewCfg] = useState({
    CompanyCode: "",
    ClubStatusRuleCode: "",
    ClubMembershipTypeCode: "",
    ExcludeEmptyClubStatusRuleCode: false,
    ExcludeEmptyClubMembershipTypeCode: false,
    IsActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [onlyActive, setOnlyActive] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await api.get("/member-filter-config", {
        params: onlyActive ? { active: true } : undefined,
      });
      setConfigs(res.data || []);
    } catch {
      showSnack("Failed to load member filter config", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyActive]);

  const handleEditChange = (id, patch) =>
    setEdited((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const handleAdd = async () => {
    if (!newCfg.CompanyCode?.trim()) {
      showSnack("CompanyCode is required", "warning");
      return;
    }
    try {
      await api.post("/member-filter-config", {
        CompanyCode: newCfg.CompanyCode.trim(),
        ClubStatusRuleCode: newCfg.ClubStatusRuleCode?.trim() || null,
        ClubMembershipTypeCode: newCfg.ClubMembershipTypeCode?.trim() || null,
        ExcludeEmptyClubStatusRuleCode: Boolean(newCfg.ExcludeEmptyClubStatusRuleCode),
        ExcludeEmptyClubMembershipTypeCode: Boolean(newCfg.ExcludeEmptyClubMembershipTypeCode),
        IsActive: Boolean(newCfg.IsActive),
      });
      setNewCfg({
        CompanyCode: "",
        ClubStatusRuleCode: "",
        ClubMembershipTypeCode: "",
        ExcludeEmptyClubStatusRuleCode: false,
        ExcludeEmptyClubMembershipTypeCode: false,
        IsActive: true,
      });
      showSnack("Config added");
      fetchConfigs();
    } catch {
      showSnack("Error adding config", "error");
    }
  };

  const handleSave = async (row) => {
    const edits = edited[row.id] || {};
    const payload = {
      CompanyCode: edits.CompanyCode ?? row.CompanyCode,
      ClubStatusRuleCode: edits.ClubStatusRuleCode ?? row.ClubStatusRuleCode,
      ClubMembershipTypeCode: edits.ClubMembershipTypeCode ?? row.ClubMembershipTypeCode,
      ExcludeEmptyClubStatusRuleCode:
        edits.ExcludeEmptyClubStatusRuleCode ?? row.ExcludeEmptyClubStatusRuleCode,
      ExcludeEmptyClubMembershipTypeCode:
        edits.ExcludeEmptyClubMembershipTypeCode ?? row.ExcludeEmptyClubMembershipTypeCode,
      IsActive: edits.IsActive ?? row.IsActive,
    };
    try {
      await api.put(`/member-filter-config/${row.id}`, payload);
      setEdited((prev) => { const c = { ...prev }; delete c[row.id]; return c; });
      showSnack("Config updated");
      fetchConfigs();
    } catch {
      showSnack("Error updating config", "error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this member filter config?")) return;
    try {
      await api.delete(`/member-filter-config/${id}`);
      showSnack("Config deleted");
      fetchConfigs();
    } catch {
      showSnack("Error deleting config", "error");
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ borderRadius: "10px", mb: 3 }}>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>How these rules work</Typography>
        <Typography variant="body2">
          Each row is an <strong>EXCLUDE</strong> rule for a CompanyCode. Set a Status Rule and/or
          Membership Type to filter members matching those criteria. <strong>Exclude Empty</strong>{" "}
          switches remove members where the field is blank or NULL.
        </Typography>
      </Alert>

      {/* Add form */}
      <Paper elevation={0} sx={{ ...paperSx, p: 2.5, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 2 }}>
          Add New Rule
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="CompanyCode *"
            value={newCfg.CompanyCode}
            onChange={(e) => setNewCfg((p) => ({ ...p, CompanyCode: e.target.value }))}
            sx={{ minWidth: 180, flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
          <TextField
            size="small"
            label="ClubStatusRuleCode"
            value={newCfg.ClubStatusRuleCode}
            onChange={(e) => setNewCfg((p) => ({ ...p, ClubStatusRuleCode: e.target.value }))}
            sx={{ minWidth: 200, flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            helperText="Optional"
          />
          <TextField
            size="small"
            label="ClubMembershipTypeCode"
            value={newCfg.ClubMembershipTypeCode}
            onChange={(e) => setNewCfg((p) => ({ ...p, ClubMembershipTypeCode: e.target.value }))}
            sx={{ minWidth: 220, flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            helperText="Optional"
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, flexWrap: "wrap" }}>
          <Tooltip title="Exclude members with blank/NULL ClubStatusRuleCode">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(newCfg.ExcludeEmptyClubStatusRuleCode)}
                  onChange={(e) =>
                    setNewCfg((p) => ({ ...p, ExcludeEmptyClubStatusRuleCode: e.target.checked }))
                  }
                />
              }
              label="Exclude Empty SR"
            />
          </Tooltip>
          <Tooltip title="Exclude members with blank/NULL ClubMembershipTypeCode">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={Boolean(newCfg.ExcludeEmptyClubMembershipTypeCode)}
                  onChange={(e) =>
                    setNewCfg((p) => ({
                      ...p,
                      ExcludeEmptyClubMembershipTypeCode: e.target.checked,
                    }))
                  }
                />
              }
              label="Exclude Empty MT"
            />
          </Tooltip>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={newCfg.IsActive}
                onChange={(e) => setNewCfg((p) => ({ ...p, IsActive: e.target.checked }))}
              />
            }
            label="Active"
          />
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={primaryBtnSx}>
            Add
          </Button>
        </Box>
      </Paper>

      {/* Existing configs */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142" }}>
          Existing Rules {configs.length > 0 && `(${configs.length})`}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={onlyActive}
                onChange={(e) => setOnlyActive(e.target.checked)}
              />
            }
            label="Only Active"
          />
          <IconButton size="small" onClick={fetchConfigs} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      {configs.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: "10px" }}>
          No rules found. Add one above.
        </Alert>
      ) : (
        configs.map((row) => {
          const edits = edited[row.id] || {};
          const merged = { ...row, ...edits };
          const dirty = Object.keys(edits).length > 0;

          return (
            <Paper
              key={row.id}
              elevation={0}
              sx={{
                ...paperSx,
                p: 2,
                mb: 1.25,
                border: dirty
                  ? "1px solid rgba(47,99,173,0.3)"
                  : "1px solid rgba(47,99,173,0.08)",
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                <Chip
                  size="small"
                  label={`#${row.id}`}
                  color={dirty ? "primary" : "default"}
                  variant={dirty ? "filled" : "outlined"}
                  sx={{ fontWeight: 700 }}
                />

                <TextField
                  size="small"
                  label="CompanyCode"
                  value={merged.CompanyCode ?? ""}
                  onChange={(e) => handleEditChange(row.id, { CompanyCode: e.target.value })}
                  sx={{ minWidth: 150, flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
                <TextField
                  size="small"
                  label="ClubStatusRuleCode"
                  value={merged.ClubStatusRuleCode ?? ""}
                  onChange={(e) => handleEditChange(row.id, { ClubStatusRuleCode: e.target.value })}
                  sx={{ minWidth: 180, flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
                <TextField
                  size="small"
                  label="ClubMembershipTypeCode"
                  value={merged.ClubMembershipTypeCode ?? ""}
                  onChange={(e) =>
                    handleEditChange(row.id, { ClubMembershipTypeCode: e.target.value })
                  }
                  sx={{ minWidth: 200, flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(merged.ExcludeEmptyClubStatusRuleCode)}
                      onChange={(e) =>
                        handleEditChange(row.id, { ExcludeEmptyClubStatusRuleCode: e.target.checked })
                      }
                    />
                  }
                  label="No Empty SR"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(merged.ExcludeEmptyClubMembershipTypeCode)}
                      onChange={(e) =>
                        handleEditChange(row.id, {
                          ExcludeEmptyClubMembershipTypeCode: e.target.checked,
                        })
                      }
                    />
                  }
                  label="No Empty MT"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(merged.IsActive)}
                      onChange={(e) => handleEditChange(row.id, { IsActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />

                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleSave(row)}
                    disabled={!dirty}
                    sx={{ color: "#2f63ad" }}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          );
        })
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── IntegrationDialog ────────────────────────────────────────────────────────

const IntegrationDialog = ({ open, integration, onClose, onSaved }) => {
  const [form, setForm] = useState(BLANK_INTEGRATION);
  const [exportTime, setExportTime] = useState(new Date());
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });

  useEffect(() => {
    if (open) {
      if (integration) {
        setForm({
          name: integration.name || "",
          type: integration.type || "jonas_jcm",
          is_active: Boolean(integration.is_active),
          frequency: integration.frequency || "manual",
          export_hour: integration.export_hour ?? 0,
          export_minute: integration.export_minute ?? 0,
        });
        const t = new Date();
        t.setHours(integration.export_hour ?? 0);
        t.setMinutes(integration.export_minute ?? 0);
        setExportTime(t);
      } else {
        setForm(BLANK_INTEGRATION);
        setExportTime(new Date());
      }
    }
  }, [open, integration]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setSnack({ open: true, message: "Name is required", severity: "warning" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        export_hour: exportTime.getHours(),
        export_minute: exportTime.getMinutes(),
        is_active: Boolean(form.is_active),
      };
      if (integration?.id) {
        await api.put(`/integrations/${integration.id}`, payload);
      } else {
        await api.post("/integrations", payload);
      }
      onSaved();
      onClose();
    } catch {
      setSnack({ open: true, message: "Error saving integration", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.3rem", pt: 3, pb: 1 }}
        >
          {integration ? "Edit Integration" : "Add Integration"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <TextField
            fullWidth
            label="Integration Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            sx={fieldSx}
          />

          <TextField
            select
            fullWidth
            label="Integration Type"
            name="type"
            value={form.type}
            onChange={handleChange}
            sx={fieldSx}
          >
            {Object.entries(INTEGRATION_TYPES).map(([val, label]) => (
              <MenuItem key={val} value={val}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Export Frequency"
            name="frequency"
            value={form.frequency}
            onChange={handleChange}
            sx={fieldSx}
          >
            {Object.entries(FREQUENCY_LABELS).map(([val, label]) => (
              <MenuItem key={val} value={val}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              label="Scheduled Export Time"
              value={exportTime}
              onChange={(v) => v && setExportTime(v)}
              ampm={false}
              sx={{ ...fieldSx, width: "100%" }}
            />
          </LocalizationProvider>

          <Box
            sx={{
              mt: 2,
              px: 1.5,
              py: 1.25,
              borderRadius: "12px",
              backgroundColor: "rgba(47,99,173,0.04)",
              border: "1px solid rgba(47,99,173,0.08)",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.is_active)}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.25 }}>
          <Button onClick={onClose} variant="outlined" sx={secondaryBtnSx} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" sx={primaryBtnSx} disabled={saving}>
            {saving ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : integration ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const IntegrationSettings = () => {
  const [integrations, setIntegrations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [sectionTab, setSectionTab] = useState(0);
  const [bottomTab, setBottomTab] = useState(0);
  const [exporting, setExporting] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const res = await api.get("/integrations");
      setIntegrations(res.data || []);
    } catch {
      showSnack("Failed to load integrations", "error");
    }
  };

  const handleOpenAdd = () => {
    setEditingIntegration(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (integration) => {
    setEditingIntegration(integration);
    setDialogOpen(true);
  };

  const handleDelete = async (integration) => {
    if (
      !window.confirm(
        `Deactivate "${integration.name}"? This will disable the integration but preserve its settings.`
      )
    )
      return;
    try {
      await api.delete(`/integrations/${integration.id}`);
      showSnack("Integration deactivated");
      fetchIntegrations();
    } catch {
      showSnack("Error deleting integration", "error");
    }
  };

  const handleExport = async (integration) => {
    setExporting(integration.id);
    try {
      const res = await api.get("/jonas-exports/download", {
        params: { integration_id: integration.id },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `export_${integration.name.replace(/\s+/g, "_")}_${Date.now()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      showSnack("Export downloaded");
    } catch {
      showSnack("Export failed", "error");
    } finally {
      setExporting(null);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Integrations
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Manage accounting and export integrations, item group mappings, and member filter rules.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
          sx={primaryBtnSx}
        >
          Add Integration
        </Button>
      </Box>

      {/* Integration cards */}
      {integrations.length === 0 ? (
        <Alert
          severity="info"
          sx={{ borderRadius: "12px", mb: 3 }}
          icon={<LinkIcon />}
        >
          No integrations configured yet. Click <strong>Add Integration</strong> to get started.
        </Alert>
      ) : (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {integrations.map((integration) => (
            <Grid item xs={12} sm={6} md={4} key={integration.id}>
              <IntegrationCard
                integration={integration}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
                onExport={handleExport}
                exporting={exporting}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Section tabs */}
      <Divider sx={{ mb: 2 }} />

      <Tabs
        value={bottomTab}
        onChange={(_, v) => setBottomTab(v)}
        sx={{
          mb: 2,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 38 },
          "& .Mui-selected": { color: "#2f63ad !important", backgroundColor: "rgba(47,99,173,0.07)", borderRadius: "8px" },
          "& .MuiTabs-indicator": { display: "none" },
        }}
      >
        <Tab label="Member Filter Rules" />
        <Tab label="Custom Fields" />
      </Tabs>

      {bottomTab === 0 && <MemberFilteringPanel />}
      {bottomTab === 1 && <CustomFieldsPanel />}

      {/* Integration edit dialog */}
      <IntegrationDialog
        open={dialogOpen}
        integration={editingIntegration}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchIntegrations}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IntegrationSettings;
